// Governance Phase 3: elections and the rest (ADR 0030; plan §4 rows marked
// phase 3, §6.7, §8 screens 2, 7 and 10). The annual election and vacancy
// elections, the slate and its eligibility rules, election votes (recorded
// and, when the board orders one, by sealed ballot), certification and the
// July 1 rollover, vacancies and officer succession, recruitment,
// succession plans, board self-assessments and the ED's reports.
//
// The pure rules here are what Platform Core decides with; the staff UI and
// myBRE only explain them. Every rule names the bylaws section it comes
// from (the bylaws enacted November 24, 2025).

import { addDays } from './finance-hub'
import { FormDefinitionSchema, type FormDefinition } from './forms'
import type { GovernanceOffice, GovernanceSettings, PersonRef, SeatClass, SeatKind, VoteThreshold } from './governance'
import { addYears, LAST_CALENDAR_FISCAL_YEAR } from './governance'
import { yesVotesNeeded } from './governance-decisions'
import { fiscalYearLabel, governanceFiscalYear } from './fiscal-calendar'
import type { AppointmentProposalView, CommitteeAppointmentProposal, NominatingView, NomineeConsentView, VacantSeatRow } from './governance-nominations'

// --------------------------------------------------------------- vocabulary

export const ELECTION_KINDS = ['annual', 'vacancy'] as const
export type ElectionKind = (typeof ELECTION_KINDS)[number]

// preparing        the nominating body builds its slate
// slate_submitted  the slate is fixed and goes in the packet; floor
//                  nominations come at the meeting
// voting           a vote on it has opened at the meeting
// voted            every slot is decided (elected, or left unfilled)
// certified        the Secretary certified the results: immutable
export const ELECTION_STATUSES = ['preparing', 'slate_submitted', 'voting', 'voted', 'certified', 'canceled'] as const
export type ElectionStatus = (typeof ELECTION_STATUSES)[number]
export const ELECTION_STATUS_LABELS: Record<ElectionStatus, string> = {
  preparing: 'Building the slate',
  slate_submitted: 'Slate submitted',
  voting: 'Voting',
  voted: 'Voted, awaiting certification',
  certified: 'Certified',
  canceled: 'Canceled',
}

// Who nominates: the Nominating Committee for the annual election (Article
// V, Section 3); the Executive Committee for an office vacancy (Article IV,
// Section 4).
export const NOMINATING_BODIES = ['nominating_committee', 'executive_committee'] as const
export type NominatingBody = (typeof NOMINATING_BODIES)[number]
export const NOMINATING_BODY_LABELS: Record<NominatingBody, string> = { nominating_committee: 'Nominating Committee', executive_committee: 'Executive Committee' }

export const SLOT_KINDS = ['director', 'office'] as const
export type SlotKind = (typeof SLOT_KINDS)[number]
export const SLOT_STATUSES = ['open', 'elected', 'unfilled'] as const
export type SlotStatus = (typeof SLOT_STATUSES)[number]

export const NOMINATION_SOURCES = ['committee', 'floor'] as const
export type NominationSource = (typeof NOMINATION_SOURCES)[number]
export const NOMINATION_STATUSES = ['standing', 'withdrawn', 'elected', 'not_elected'] as const
export type NominationStatus = (typeof NOMINATION_STATUSES)[number]

// recorded  each director's vote for a candidate is recorded by name, as
//           every vote of the board is (plan §2.4)
// ballot    only when the board orders a ballot by motion (Robert's Rules;
//           plan §6.7): the sealed ballot module, choices never linked to
//           a voter
export const ROUND_METHODS = ['recorded', 'ballot'] as const
export type RoundMethod = (typeof ROUND_METHODS)[number]
export const ROUND_STATUSES = ['open', 'elected', 'no_majority', 'no_quorum'] as const
export type RoundStatus = (typeof ROUND_STATUSES)[number]
export const ROUND_STATUS_LABELS: Record<RoundStatus, string> = { open: 'Voting open', elected: 'Elected', no_majority: 'No majority: vote again', no_quorum: 'No quorum: decided nothing' }

export const PROSPECT_STAGES = ['identified', 'contacted', 'interested', 'vetted', 'nominated', 'declined'] as const
export type ProspectStage = (typeof PROSPECT_STAGES)[number]
export const PROSPECT_STAGE_LABELS: Record<ProspectStage, string> = { identified: 'Identified', contacted: 'Contacted', interested: 'Interested', vetted: 'Vetted', nominated: 'Nominated', declined: 'Declined' }

export const VACANCY_KINDS = ['director', 'office'] as const
export type VacancyKind = (typeof VACANCY_KINDS)[number]
export const VACANCY_STATUSES = ['open', 'filled', 'closed'] as const
export type VacancyStatus = (typeof VACANCY_STATUSES)[number]

// The motion that orders an election by ballot is an ordinary motion
// (Robert's Rules: a majority of those present and voting).
export const BALLOT_MOTION_TYPE = 'ordinary'

// ------------------------------------------------------------- the calendar

// The offices whose two-year terms begin in a calendar year (Article IV,
// Section 2): President, Vice President and Treasurer in even years,
// Secretary and Past President in odd years.
export function officesUpIn(year: number, settings: Pick<GovernanceSettings, 'offices'>): GovernanceOffice[] {
  return [...(year % 2 === 0 ? settings.offices.evenYearStart : settings.offices.oddYearStart)]
}

// The annual election held in fiscal year N (at the end of its final
// regular meeting, Article III, Section 8) elects terms that begin on the
// first day of fiscal year N+1 (Article VII, Section 1). From the short
// year on that is July 1 of calendar year N: the short year's annual
// meeting (June 2026) elected the terms beginning July 1, 2026, and
// FY2027's (June 2027) elects terms beginning July 1, 2027. Before 2026
// the fiscal year was the calendar year and terms began January 1 of
// N+1 (ADR 0050); FY2025's would have been January 1, 2026, but those
// terms were extended to June 30, 2026 instead.
export function annualTermsStartOn(fiscalYear: number, settings: Pick<GovernanceSettings, 'fiscalYear'>): string {
  if (fiscalYear <= LAST_CALENDAR_FISCAL_YEAR) return `${fiscalYear + 1}-01-01`
  return `${fiscalYear}-${settings.fiscalYear.termsStartMonthDay}`
}

// The last day of a full term of `years` beginning on `startsOn`.
export function termLastDay(startsOn: string, years: number): string {
  return addDays(addYears(startsOn, years), -1)
}

// ---------------------------------------------------------- eligibility

// What core gathers about a nominee for a slot; nominationProblems decides.
export type NomineeFacts = {
  slot: { kind: SlotKind; office: GovernanceOffice | null; label: string; termStartsOn: string; termEndsOn: string; atLarge: boolean }
  name: string
  // Director slot: Article III, Sections 3 and 3.1 (Phase 1's computation,
  // with an override adopted by the board).
  termLimit: { eligible: boolean; reason: string; overridden: boolean } | null
  // Director slot: a term on another seat that would overlap the new one.
  otherSeat: string | null
  // Director slot: already standing for another director slot here.
  otherDirectorNomination: string | null
  // Office slot: whether their director service covers the office term
  // (Article IV, Section 1), now or only if elected a director in this
  // same election (which seat).
  directorCover: 'covers' | 'no' | { ifElectedTo: string }
  // Office slot: the City's appointed seat during the office term.
  appointedSeat: boolean
  // Office slot: another office held, or stood for here, in that period.
  otherOffice: string | null
  // Office slot: a Nominating Committee member other than the Vice
  // President and Past President this fiscal year (Article V, Section 3).
  nominatingMember: boolean
  // Past President slot (Article IV, Section 5c).
  formerPresident: { id: string; name: string } | null
  isFormerPresident: boolean
}

export type Eligibility = { ok: boolean; problems: string[]; conditional: string | null }

export function nominationProblems(f: NomineeFacts, settings: Pick<GovernanceSettings, 'offices' | 'committees'>): Eligibility {
  const problems: string[] = []
  let conditional: string | null = null
  if (f.slot.kind === 'director') {
    if (f.termLimit && !f.termLimit.eligible && !f.termLimit.overridden) problems.push(`${f.name}: ${f.termLimit.reason}`)
    if (f.otherSeat) problems.push(`${f.name} ${f.otherSeat}; a director holds one seat`)
    if (f.otherDirectorNomination) problems.push(`${f.name} already stands for ${f.otherDirectorNomination} in this election`)
    return { ok: problems.length === 0, problems, conditional }
  }
  if (f.appointedSeat && !settings.offices.appointedSeatMayHoldOffice) problems.push(`The City's appointed director can't hold office`)
  if (f.directorCover === 'no') problems.push(`An officer must be a director for the whole term, and ${f.name}'s director term doesn't cover ${f.slot.termStartsOn} to ${f.slot.termEndsOn}`)
  else if (typeof f.directorCover === 'object') conditional = `Eligible only if elected a director (${f.directorCover.ifElectedTo}) first in this election`
  if (f.otherOffice && settings.offices.onePerPerson) problems.push(`${f.name} ${f.otherOffice}: one office per person`)
  if (f.nominatingMember && settings.committees.nominatingMembersIneligibleForOffice) problems.push(`${f.name} serves on the Nominating Committee and isn't eligible for office`)
  if (f.slot.office === 'past_president' && !f.slot.atLarge) {
    if (!f.formerPresident) problems.push('No former President to serve as Past President; fill it as a Director at Large')
    else if (!f.isFormerPresident) problems.push(`The Past President is the immediate former President, ${f.formerPresident.name}`)
  }
  return { ok: problems.length === 0, problems, conditional }
}

// ---------------------------------------------------------------- counting

export type RoundCount = { nominationId: string; name: string; votes: number }

export type RoundResult = {
  outcome: 'elected' | 'no_majority' | 'no_quorum'
  electedNominationId: string | null
  counts: RoundCount[]
  abstain: number
  // What the threshold is measured against, and how many votes it took.
  base: number
  baseLabel: string
  needed: number
  quorum: { required: number; present: number; met: boolean }
  explanation: string
}

// A round of voting on one contested slot (or any slot once a ballot is
// ordered). Electing is a majority of the directors present, with quorum
// (Article III, Section 4); the Past President seat filled as a Director
// at Large is a majority of the board (Article IV, Section 5c). A
// candidate is elected with at least that many votes; otherwise no one is,
// and the board votes again (Robert's Rules: no candidate is dropped
// unless they withdraw). Abstentions and blank ballots count toward no one.
export function roundResult(input: {
  threshold: Pick<VoteThreshold, 'rule' | 'numerator' | 'denominator' | 'base' | 'source'>
  counts: RoundCount[]
  abstain: number
  presentVoting: number
  seatedVoting: number
  quorumRequired: number
}): RoundResult {
  const quorum = { required: input.quorumRequired, present: input.presentVoting, met: input.quorumRequired > 0 && input.presentVoting >= input.quorumRequired }
  const [base, baseLabel] = input.threshold.base === 'board' ? [input.seatedVoting, 'directors seated'] : [input.presentVoting, 'directors present']
  const needed = yesVotesNeeded(input.threshold, base)
  const counts = [...input.counts].sort((a, b) => b.votes - a.votes || a.name.localeCompare(b.name))
  if (!quorum.met) return { outcome: 'no_quorum', electedNominationId: null, counts, abstain: input.abstain, base, baseLabel, needed, quorum, explanation: `No quorum when the vote closed (${quorum.present} of ${quorum.required} needed)` }
  const winner = counts.find((c) => base > 0 && c.votes >= needed) ?? null
  const tallyText = counts.map((c) => `${c.name} ${c.votes}`).join(', ')
  return winner
    ? { outcome: 'elected', electedNominationId: winner.nominationId, counts, abstain: input.abstain, base, baseLabel, needed, quorum, explanation: `${winner.name} elected with ${winner.votes} of ${base} ${baseLabel}; ${needed} needed (${tallyText})` }
    : { outcome: 'no_majority', electedNominationId: null, counts, abstain: input.abstain, base, baseLabel, needed, quorum, explanation: `No candidate reached ${needed} of ${base} ${baseLabel} (${tallyText || 'no votes'}${input.abstain ? `, ${input.abstain} abstaining or blank` : ''}); vote again` }
}

// ------------------------------------------------------------------ shapes

export type NominationView = {
  id: string
  person: PersonRef
  source: NominationSource
  nominatedBy: PersonRef | null
  channel: 'mybre' | 'hub'
  status: NominationStatus
  eligibility: Eligibility
  prospectId: string | null
  withdrawnReason: string | null
  createdAt: string
  // ADR 0060: on the Nominating Committee's slate, the nominee's consent to
  // serve and their bio; null otherwise.
  consent?: NomineeConsentView | null
}

export type RoundView = {
  id: string
  slotId: string
  number: number
  method: RoundMethod
  status: RoundStatus
  openedAt: string
  closedAt: string | null
  candidates: { nominationId: string; name: string }[]
  threshold: { label: string; description: string; base: string; source: string } | null
  // Only once the round is closed: while a ballot is open even the counts
  // would tell who voted how, one receipt at a time.
  result: RoundResult | null
  // A recorded round's votes, by name (plan §2.4). Never for a ballot.
  votes: { person: PersonRef; nominationId: string | null; channel: 'mybre' | 'roll_call' | 'attested' }[]
  // Who has voted (a ballot's receipts: who, never what).
  voted: PersonRef[]
  awaiting: PersonRef[]
}

export type ElectionSlotView = {
  id: string
  position: number
  kind: SlotKind
  label: string
  seatId: string | null
  seatClass: SeatClass | null
  office: GovernanceOffice | null
  termStartsOn: string
  termEndsOn: string
  partial: boolean
  atLarge: boolean
  atLargeReason: string | null
  incumbent: PersonRef | null
  status: SlotStatus
  elected: PersonRef | null
  decidedBy: { kind: 'motion' | 'round'; id: string } | null
  unfilledReason: string | null
  thresholdType: string
  thresholdText: string
  contested: boolean
  nominations: NominationView[]
  rounds: RoundView[]
}

export type ElectionStage = { key: string; title: string; status: 'done' | 'current' | 'todo' | 'warning'; detail: string; source: string }

export type RolloverItem = {
  kind: 'director' | 'office'
  person: PersonRef
  label: string
  startsOn: string
  endsOn: string
  beganBy: string
  onboarding: boolean
  replaces: { person: PersonRef; lastDay: string } | null
  // Not elected yet: the slate's nominee, shown as what certification
  // would create.
  projected: boolean
}

export type ElectionView = {
  id: string
  kind: ElectionKind
  fiscalYear: number
  title: string
  nominatingBody: NominatingBody
  status: ElectionStatus
  rehearsal: boolean
  meeting: { id: string; title: string; startsLocal: string; status: string; kind: string } | null
  expiringClass: SeatClass | null
  termsStartOn: string
  slots: ElectionSlotView[]
  slateSubmitted: { at: string; by: PersonRef | null; channel: 'mybre' | 'attested'; attestation: string | null } | null
  ballot: { motionId: string; orderedAt: string | null } | null
  certification: { at: string; by: PersonRef | null; channel: 'mybre' | 'attested'; attestation: string | null; hash: string } | null
  results: ElectionResults | null
  stages: ElectionStage[]
  rollover: RolloverItem[]
  vacancyId: string | null
  warnings: string[]
  motions: { id: string; title: string; status: string; kind: 'slate' | 'slot' | 'ballot'; slotIds: string[] }[]
  // ADR 0060: the Nominating Committee's timeline, adoption and report
  // (the annual election only).
  nominating?: NominatingView | null
  // ADR 0081: vacant seats not up this year, and every name proposed for one.
  vacantSeats?: VacantSeatRow[]
  appointmentProposals?: AppointmentProposalView[]
  version: number
}

// ------------------------------------------------- elections held before Hub

// An annual election is named for the fiscal year its terms serve, as the
// board says it: the June 2026 annual meeting (held in the short year)
// elected the FY2027 board, so it is "the annual election for FY2027".
// Records keep the fiscal year it was held in (`fiscalYear`); this only
// names it.
export function termsFiscalYear(termsStartOn: string): number {
  return governanceFiscalYear(termsStartOn)
}
export function annualElectionName(termsStartOn: string): string {
  return `Annual election for ${fiscalYearLabel(termsFiscalYear(termsStartOn))}`
}

// Where an annual election stands on the Elections page (ADR 0030
// amendment, 2026-10-26). An election whose terms have begun is never
// "not started": it was run in Hub, recorded from records, or held before
// Hub and not recorded yet (neutral, not an error).
export type AnnualPlanState = 'hub' | 'from_records' | 'not_recorded' | 'not_started'
export function annualPlanState(input: { termsStartOn: string; today: string; hubElection: boolean; fromRecords: boolean }): AnnualPlanState {
  if (input.hubElection) return 'hub'
  if (input.fromRecords) return 'from_records'
  return input.termsStartOn <= input.today ? 'not_recorded' : 'not_started'
}

export const ANNUAL_PLAN_STATE_LABELS: Record<AnnualPlanState, string> = {
  hub: 'In Hub',
  from_records: 'From records',
  not_recorded: 'Held before Hub, not recorded',
  not_started: 'Not started',
}

// A term or office that began on an election's first day, read from the
// terms each time (never copied into the election).
export type TermBegunView = { kind: 'director' | 'office'; person: PersonRef; label: string; startsOn: string; endsOn: string; fromRecords: boolean }

export type RecordMeetingRef = { source: 'hub' | 'records'; id: string; title: string; heldOn: string; kind: string; minutesId: string | null; sourceFile: string | null }

export type ElectionFromRecordsView = {
  id: string
  fiscalYear: number
  termsStartOn: string
  heldOn: string
  // A Hub meeting (with its minutes, if any) or a meeting from records
  // (with the file its minutes came from); null when only the date is known.
  meeting: RecordMeetingRef | null
  resolutions: { id: string; number: string; title: string; adoptedOn: string }[]
  note: string | null
  recordedBy: PersonRef
  recordedAt: string
}

export type AnnualPlanView = {
  // The fiscal year the election is held in (election.fiscal_year).
  fiscalYear: number
  // The fiscal year its terms serve, and its name.
  termsFiscalYear: number
  name: string
  termsStartOn: string
  termsBegun: boolean
  state: AnnualPlanState
  // Up for election (for an election not yet held; empty once terms began).
  expiringClass: SeatClass | null
  seats: number
  offices: GovernanceOffice[]
  warnings: string[]
  election: string | null
  fromRecords: ElectionFromRecordsView | null
  // Computed, for an election whose terms have begun.
  stages: ElectionStage[]
  termsBegunList: TermBegunView[]
  // To record it (governance.manage, state not_recorded only).
  recordOptions: { meetings: RecordMeetingRef[]; resolutions: { id: string; number: string; title: string; adoptedOn: string }[] } | null
}

// What the Secretary certifies (frozen, with its SHA-256).
export type ElectionResults = {
  electionId: string
  title: string
  fiscalYear: number
  rehearsal: boolean
  meeting: { id: string; title: string; startsLocal: string } | null
  method: 'recorded' | 'ballot' | 'mixed'
  slots: {
    label: string
    kind: SlotKind
    termStartsOn: string
    termEndsOn: string
    status: SlotStatus
    elected: PersonRef | null
    how: string
    rounds: { number: number; method: RoundMethod; status: RoundStatus; explanation: string }[]
  }[]
}

export type ProspectView = {
  id: string
  person: PersonRef | null
  proposedName: string | null
  stage: ProspectStage
  owner: PersonRef | null
  source: string | null
  election: { id: string; title: string } | null
  notes: { id: string; body: string; at: string; by: string | null }[]
  history: { stage: ProspectStage; at: string; by: string | null }[]
  // ADR 0060: from the public interest form. Its answers for the Nominating
  // Committee, never the contact details or a field marked sensitive (the
  // ED reaches those in Forms); none once the submission is redacted.
  interest: { submissionId: string; submittedAt: string; answers: { label: string; value: string }[] } | null
  version: number
  updatedAt: string
}

export type VacancyView = {
  id: string
  kind: VacancyKind
  seat: { id: string; label: string; seatClass: SeatClass | null; kind: SeatKind } | null
  office: GovernanceOffice | null
  vacated: PersonRef | null
  openedOn: string
  termEndsOn: string
  status: VacancyStatus
  reason: string
  filledBy: PersonRef | null
  filledOn: string | null
  appointmentMotion: { id: string; title: string; status: string; meetingId: string | null; appointee: PersonRef | null } | null
  election: { id: string; title: string; status: ElectionStatus } | null
  succession: { from: PersonRef; office: GovernanceOffice; on: string } | null
  // ADR 0081: the name on the slate the Nominating Committee adopted. The
  // President may appoint someone else.
  committeeProposal?: CommitteeAppointmentProposal | null
}

// An unretired seat no term covers today (ignoring voided terms): never
// filled, or its last term ended with no successor. An elected seat is
// filled for the rest of its class's current term by the President's
// appointment, once the ED records it as a vacancy; the City's seat waits
// for the City Council's appointment (Article III, Sections 2.1 and 11).
export type EmptySeatView = {
  seat: { id: string; label: string; seatClass: SeatClass | null; kind: SeatKind }
  // The day after its last term, or null when no term is on record.
  emptySince: string | null
  lastHolder: PersonRef | null
  // The term the seat is in now: its class's current term, or the City
  // seat's calendar-year term. Null when nothing on record shows it.
  currentTerm: { startsOn: string; endsOn: string } | null
  // A term already entered that begins later.
  nextTerm: { person: PersonRef; startsOn: string } | null
  filledBy: 'board_appointment' | 'city_appointment'
}

export type DutyMarkerView = { id: string; person: PersonRef; performs: GovernanceOffice; fromOn: string; untilOn: string | null; reason: string; endedReason: string | null }

export type SuccessionPlanView = {
  office: GovernanceOffice
  holder: PersonRef | null
  successor: PersonRef | null
  notes: string | null
  updatedAt: string | null
  updatedBy: string | null
  version: number
  // Article IV, Section 4, applied by Hub on its own.
  rule: string
}

export type AssessmentSummary = { id: string; title: string; fiscalYear: number; status: 'open' | 'closed'; sentAt: string; dueOn: string | null; recipients: number; responses: number; closedAt: string | null }

export type AssessmentAggregate = {
  questions: { key: string; label: string; type: string; options: string[]; counts: Record<string, number>; answered: number }[]
  responses: number
  recipients: number
}

// Version 1 of the board self-assessment (plan §4: "Board
// self-assessments: reuse Hub forms"), a forms engine definition. The ED
// may send it as it is or with changed questions; each assessment keeps
// the definition it was sent with. Individual answers are confidential
// (the ED and the Executive Committee); the aggregate goes to the ED and
// the Executive Committee.
const AGREE = ['Strongly agree', 'Agree', 'Neutral', 'Disagree', 'Strongly disagree']
export const BOARD_ASSESSMENT_DEFINITION: FormDefinition = FormDefinitionSchema.parse({
  title: 'Board self-assessment',
  intro:
    'Each director answers on their own. Your individual answers are read only by the Executive Director and the Executive Committee; the board sees the totals. It takes about five minutes.',
  fields: [
    { key: 'mission', type: 'single_choice', label: 'The board understands BRE’s mission and keeps its decisions tied to it.', options: AGREE, required: true },
    { key: 'meetings', type: 'single_choice', label: 'Board meetings are well run, focused on decisions, and a good use of my time.', options: AGREE, required: true },
    { key: 'packets', type: 'single_choice', label: 'I get the packet and the information I need in time to prepare.', options: AGREE, required: true },
    { key: 'finance', type: 'single_choice', label: 'The board understands BRE’s finances well enough to oversee them.', options: AGREE, required: true },
    { key: 'ed', type: 'single_choice', label: 'The board and the Executive Director work well together, with clear roles.', options: AGREE, required: true },
    { key: 'committees', type: 'single_choice', label: 'Committees do real work and report clearly to the board.', options: AGREE, required: true },
    { key: 'recruiting', type: 'single_choice', label: 'The board recruits and orients new directors well.', options: AGREE, required: true },
    { key: 'contribution', type: 'single_choice', label: 'I contribute my skills and time to the board as I intended to.', options: AGREE, required: true },
    { key: 'best', type: 'long_text', label: 'What is the board doing best?', maxLength: 2000 },
    { key: 'improve', type: 'long_text', label: 'What should the board change next year?', maxLength: 2000 },
  ],
  confirmationMessage: 'Thank you. Your answers are in.',
  submitLabel: 'Submit my answers',
})

export type ReportDirectorRow = {
  person: PersonRef
  seat: string
  seatClass: SeatClass | null
  termEnds: string
  attendance: { meetings: number; present: number; presentElectronically: number; excused: number; unexcused: number; flagged: boolean }
  participation: { motionsPresent: number; votesCast: number; recused: number; emailMeetings: number; emailResponses: number; electionVotesPresent: number; electionVotesCast: number }
  acknowledgments: { policiesRequested: number; policiesAcknowledged: number; packets: number; packetsAcknowledged: number }
  disclosureFiled: boolean
  nextTerm: { startsOn: string; eligible: boolean; reason: string; overridden: boolean } | null
}

export type GovernanceReport = {
  fiscalYear: number
  from: string
  to: string
  board: { seated: number; minDirectors: number; maxDirectors: number; byClass: Record<string, number>; appointed: number; warnings: string[] }
  meetingsHeld: number
  directors: ReportDirectorRow[]
  termLimitOutlook: { person: PersonRef; seat: string; lastDay: string; reason: string }[]
}

export const OFFICE_SUCCESSION_RULES: Record<GovernanceOffice, string> = {
  president: 'The Vice President becomes President. If both offices are vacant, the Secretary acts as President until the board elects one.',
  vice_president: 'The board fills it from the Executive Committee’s nominations.',
  past_president: 'The board fills it from the Executive Committee’s nominations, or with a Director at Large.',
  secretary: 'The board fills it from the Executive Committee’s nominations.',
  treasurer: 'The board fills it from the Executive Committee’s nominations.',
}

// ------------------------------------------------------------ resignations

// Article III, Section 10 and Article IV, Section 3: a resignation is in
// writing and received by the President or the Secretary. Scope 'board'
// leaves the board (and with it any office); 'office' leaves one office.
export const RESIGNATION_SCOPES = ['board', 'office'] as const
export type ResignationScope = (typeof RESIGNATION_SCOPES)[number]
export const RESIGNATION_STATUSES = ['delivered', 'received', 'effective', 'withdrawn'] as const
export type ResignationStatus = (typeof RESIGNATION_STATUSES)[number]
export const RESIGNATION_STATUS_LABELS: Record<ResignationStatus, string> = {
  delivered: 'Delivered, waiting for the President or Secretary to receive it',
  received: 'Received',
  effective: 'In effect',
  withdrawn: 'Withdrawn',
}

export type ResignationView = {
  id: string
  person: PersonRef
  scope: ResignationScope
  office: GovernanceOffice | null
  text: string | null
  document: { id: string; title: string } | null
  channel: 'mybre' | 'recorded'
  submittedAt: string
  effectiveOn: string
  status: ResignationStatus
  received: { at: string; by: PersonRef; office: 'president' | 'secretary'; channel: 'mybre' | 'attested'; attestation: string | null } | null
  effectiveAt: string | null
  withdrawn: { at: string; reason: string } | null
  reportedMeeting: { id: string; title: string } | null
  effects: { termEnded: boolean; officesEnded: string[]; vacancies: number } | null
}
